import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';

const formatPhoneNumber = (value) => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const isValidPhone = (val) => {
  const c = val.replace(/\D/g, '');
  return c.length === 10 || c.length === 11;
};

const IntakeContactForm = ({ onSubmit, isSubmitting, error }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!firstName.trim()) errs.firstName = 'Please complete this field';
    if (!email.trim()) errs.email = 'Please complete this field';
    if (!phone.trim()) errs.phone = 'Please complete this field';
    else if (!isValidPhone(phone)) errs.phone = 'Please enter a valid US phone number';

    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    onSubmit({
      firstName: firstName.trim(),
      email,
      phone: phone.replace(/\D/g, ''),
      smsConsent,
      marketingConsent,
    });
  };

  const clearErr = (key) => {
    if (fieldErrors[key]) setFieldErrors((p) => ({ ...p, [key]: null }));
  };

  return (
    <div className="assessment-complete-capture">
      <div className="assessment-complete-capture-content">
        <div className="capture-icon">
          <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="36" cy="36" r="34" stroke="#CBB26A" strokeWidth="2" />
            <path d="M24 36L32 44L48 28" stroke="#CBB26A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h1 className="capture-title">Your Personalized Results Are Ready</h1>
        <p className="capture-business-name">Glow Health LLC</p>
        <p className="capture-headline-sub">Where should we send them?</p>

        <div className="capture-form-section">
          <p className="capture-form-description">
            We've created a personalized hormone summary based on your answers. Enter your
            information below and we'll send your personalized results instantly.
          </p>

          <form onSubmit={handleSubmit} className="capture-form">
            <div className="capture-form-field">
              <label className="capture-label">First Name <span className="required">*</span></label>
              <input
                type="text"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); clearErr('firstName'); }}
                className={`capture-input ${fieldErrors.firstName ? 'input-error' : ''}`}
                data-testid="assessment-firstname-input"
              />
              {fieldErrors.firstName && <span className="field-error-message">{fieldErrors.firstName}</span>}
            </div>

            <div className="capture-form-field">
              <label className="capture-label">Email Address <span className="required">*</span></label>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearErr('email'); }}
                className={`capture-input ${fieldErrors.email ? 'input-error' : ''}`}
                data-testid="assessment-email-input"
              />
              {fieldErrors.email && <span className="field-error-message">{fieldErrors.email}</span>}
            </div>

            <div className="capture-form-field">
              <label className="capture-label">Mobile Phone <span className="required">*</span></label>
              <input
                type="tel"
                placeholder="(555) 555-5555"
                value={phone}
                onChange={(e) => { setPhone(formatPhoneNumber(e.target.value)); clearErr('phone'); }}
                className={`capture-input ${fieldErrors.phone ? 'input-error' : ''}`}
                data-testid="assessment-phone-input"
              />
              {fieldErrors.phone && <span className="field-error-message">{fieldErrors.phone}</span>}
              <p className="phone-privacy-disclaimer">
                We respect your privacy. Your number will never be sold or shared. It is only used by Glow Health LLC so our care team can contact you about your results or services if requested.
              </p>
            </div>

            <div className="sms-consent-field">
              <input
                type="checkbox"
                id="sms-consent"
                name="sms_consent"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="sms-consent-checkbox"
                data-testid="sms-consent-checkbox"
              />
              <label htmlFor="sms-consent" className="sms-consent-label">
                I agree to receive SMS messages from Glow Health LLC regarding scheduling, appointment reminders, and care coordination. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out. Reply HELP for assistance. View our <a href="https://myglowhealth.co/privacy-policy" className="consent-link">Privacy Policy</a> and <a href="https://myglowhealth.co/terms-of-service" className="consent-link">Terms of Service</a>.
              </label>
            </div>

            <div className="sms-consent-field">
              <input
                type="checkbox"
                id="marketing-consent"
                name="marketing_consent"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="sms-consent-checkbox"
                data-testid="marketing-consent-checkbox"
              />
              <label htmlFor="marketing-consent" className="sms-consent-label">
                I agree to receive marketing and promotional SMS messages from Glow Health LLC (offers, updates, announcements). Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out. Reply HELP for assistance. View our <a href="https://myglowhealth.co/privacy-policy" className="consent-link">Privacy Policy</a> and <a href="https://myglowhealth.co/terms-of-service" className="consent-link">Terms of Service</a>.
              </label>
            </div>

            <p className="consent-disclaimer">Consent is not a condition of purchase.</p>

            {error && <p className="capture-error">{error}</p>}

            <div className="capture-trust-signals">
              <span className="trust-signal"><Check size={14} /> Private &amp; confidential</span>
              <span className="trust-signal"><Check size={14} /> Reviewed by a clinician</span>
              <span className="trust-signal"><Check size={14} /> No spam, ever</span>
            </div>

            <button
              type="submit"
              className="btn-capture-submit"
              disabled={isSubmitting}
              data-testid="submit-email-btn"
            >
              {isSubmitting ? 'Sending...' : 'Send My Results Now'}
              {!isSubmitting && <ArrowRight size={18} />}
            </button>

            <p className="compliance-text">
              Your information is private and will never be sold. View our{' '}
              <a href="/privacy-policy" className="compliance-link">Privacy Policy</a>
              {' '}and{' '}
              <a href="/terms-of-service" className="compliance-link">Terms of Service</a>.
            </p>
          </form>

          <p className="capture-privacy">
            Glow Health LLC respects your privacy and will never share your information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IntakeContactForm;
